import { Alert, Checkbox, Form, Select, Typography } from "antd";
import { useEffect, useState } from "react";
import { cadastroCotaUsuarioApi } from "../cadastroCotaUsuario.api";
import { FluxoCota, FluxoResposta } from "../cadastroCotaUsuario.types";
import {
  filterSelectOption,
  OnboardingFrame,
  SubmitButton,
} from "../utils";

interface Props {
  fluxo: FluxoResposta;
  submitting: boolean;
  onConcluir: (payload: Record<string, unknown>) => Promise<void>;
}

export function EtapaIdentificacaoCota({
  fluxo,
  submitting,
  onConcluir,
}: Props) {
  const [form] = Form.useForm();
  const [cotasFallback, setCotasFallback] = useState<FluxoCota[]>([]);
  const dadosIdentificacao =
    ((fluxo.dadosPreenchidos?.identificacaoCota ?? fluxo.dadosPreenchidos?.cota) as
      | Record<string, unknown>
      | undefined) ?? {};
  const preferenciaNaoSeiKey = `cadastro-cota-usuario:${fluxo.cadastroCotaUsuarioId}:nao-sei-cota`;
  const preferenciaNaoSei =
    typeof window !== "undefined" &&
    window.sessionStorage.getItem(preferenciaNaoSeiKey) === "true";
  const cotaSelecionadaId =
    (dadosIdentificacao.cotaId as number | undefined) ?? fluxo.contextoFluxo.cotaId;
  const identificacaoNaoSei =
    dadosIdentificacao.naoSei === true ||
    dadosIdentificacao.naoSeiNumeroCota === true ||
    (Object.prototype.hasOwnProperty.call(dadosIdentificacao, "numeroCota") &&
      dadosIdentificacao.numeroCota === null) ||
    preferenciaNaoSei;

  console.log("[IdentificacaoCota] estado recebido", {
    cadastroCotaUsuarioId: fluxo.cadastroCotaUsuarioId,
    etapaAtual: fluxo.etapaAtual,
    dadosPreenchidos: fluxo.dadosPreenchidos,
    dadosIdentificacao,
    contextoFluxo: fluxo.contextoFluxo,
    detalhesEtapa: fluxo.detalhesEtapa,
    preferenciaNaoSei,
    identificacaoNaoSei,
    cotaSelecionadaId,
  });
  const cotasRecebidas = (fluxo.detalhesEtapa?.cotas ?? []) as FluxoCota[];
  useEffect(() => {
    if (cotasRecebidas.length || !fluxo.contextoFluxo.aptoId) return;
    cadastroCotaUsuarioApi.buscarCotas().then((cotas) => {
      setCotasFallback(cotas.filter((cota) => cota.aptoId === fluxo.contextoFluxo.aptoId));
    });
  }, [cotasRecebidas.length, fluxo.contextoFluxo.aptoId]);
  const cotasBase = cotasRecebidas.length ? cotasRecebidas : cotasFallback;
  const cotas = cotaSelecionadaId && !cotasBase.some((cota) => cota.id === cotaSelecionadaId)
    ? [...cotasBase, { id: cotaSelecionadaId, numeroCota: dadosIdentificacao.numeroCota as number | null }]
    : cotasBase;
  const naoSei = Form.useWatch("naoSei", form) ?? false;

  useEffect(() => {
    form.setFieldsValue({
      cotaId: identificacaoNaoSei ? undefined : cotaSelecionadaId,
      naoSei: identificacaoNaoSei,
    });
  }, [cotaSelecionadaId, form, identificacaoNaoSei]);

  return (
    <OnboardingFrame
      fluxo={fluxo}
      title="Identifique a cota"
      description="Informe o número que identifica a sua cota."
    >
      <div style={{ marginBottom: 24 }}>
        <Typography.Paragraph>
          <strong>Qual é o número que identifica sua cota?</strong>
        </Typography.Paragraph>
        <Typography.Paragraph>
          Esse número normalmente aparece junto ao número do apartamento em seu
          contrato ou nos documentos do empreendimento.
        </Typography.Paragraph>
        <Typography.Paragraph>
          Por exemplo, se estiver escrito <strong>105/21</strong>, o apartamento
          é 105 e o número da cota é 21.
        </Typography.Paragraph>
        <Typography.Paragraph style={{ marginBottom: 0 }}>
          Não estamos perguntando quantas cotas você possui.
        </Typography.Paragraph>
      </div>

      {!cotas.length ? (
        <Alert
          type="warning"
          showIcon
          message="Nenhuma cota elegível foi recebida nesta etapa."
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          cotaId: identificacaoNaoSei ? undefined : cotaSelecionadaId,
          naoSei: identificacaoNaoSei,
        }}
        onFinish={(values) =>
          (() => {
            if (values.naoSei) {
              window.sessionStorage.setItem(preferenciaNaoSeiKey, "true");
              console.log("[IdentificacaoCota] enviando opção não sei", {
                cadastroCotaUsuarioId: fluxo.cadastroCotaUsuarioId,
                values,
                payload: { cotaId: null, naoSei: true },
              });
              return onConcluir({ cotaId: null, naoSei: true });
            }
            window.sessionStorage.removeItem(preferenciaNaoSeiKey);
            console.log("[IdentificacaoCota] enviando cota selecionada", {
              cadastroCotaUsuarioId: fluxo.cadastroCotaUsuarioId,
              values,
              payload: { cotaId: values.cotaId },
            });
            return onConcluir({ cotaId: values.cotaId });
          })()
        }
      >
        <Form.Item
          label="Número da cota"
          name="cotaId"
          rules={naoSei ? [] : [{ required: true, message: "Selecione a cota." }]}
        >
          <Select
            showSearch
            disabled={naoSei}
            optionFilterProp="label"
            filterOption={filterSelectOption}
            placeholder="Ex.: 21"
            onChange={() => form.setFieldValue("naoSei", false)}
            options={cotas.map((cota) => ({
              label: cota.numeroCota
                ? `Cota ${cota.numeroCota}`
                : `Cota ${cota.id}`,
              value: cota.id,
            }))}
          />
        </Form.Item>

        <Form.Item name="naoSei" valuePropName="checked">
          <Checkbox
            onChange={(event) => {
              if (event.target.checked) {
                form.setFieldValue("cotaId", undefined);
              }
            }}
          >
            Não sei o número da minha cota
          </Checkbox>
        </Form.Item>

        {naoSei ? (
          <Alert
            type="info"
            showIcon
            message="Sem problema. Você pode informar depois."
            description="O número da cota nos ajuda a identificar automaticamente os seus períodos no cronograma do empreendimento. Sem ele, você poderá informar os períodos manualmente."
            style={{ marginBottom: 24 }}
          />
        ) : null}

        <SubmitButton label="Continuar" loading={submitting} />
      </Form>
    </OnboardingFrame>
  );
}
